import { computed, inject, Injectable } from '@angular/core';
import { BaseWorkflowStateService } from '../../base-workflow-state.service';
import { VideoAdditionalChannelOffersData } from '../../../../../../../models/order/workflow/video/video-additional-offer.model';
import { VideoFlowStateService } from '../video-flow-state.service';
import { VideoProductResponseService } from '../../../order-api-response/video-pd-response.service';
import { Choice } from '../../../../../../../models/order/api-models/order-pd-response.model';



@Injectable({ providedIn: 'root' })
export class VideoAdditionalChannelOffersStateService extends BaseWorkflowStateService<VideoAdditionalChannelOffersData> {
    private flowStateService = inject(VideoFlowStateService);
    private videoPdResponseService = inject(VideoProductResponseService);
    override stateKey: string = 'videoAdditionalChannelOffers';
    customizations = computed(() => this.videoPdResponseService.getCustomizations()?.filter(cust =>
        cust.customization.DetailedGroupName === 'InternationalOptionalOffers' ||
        cust.customization.DetailedGroupName === 'SportsOptionalOffers' ||
        cust.customization.DetailedGroupName === 'ChannelOptionalOffers' ||
        cust.customization.DetailedGroupName === 'SeasonalChannelOptionalOffers' ||
        cust.customization.SubGroupName === 'MLBExtraInnings' ||
        cust.customization.SubGroupName === 'ESPNFullCourt' ||
        cust.customization.SubGroupName === 'NBALeaguePass' ||
        cust.customization.SubGroupName === 'NHLCenterIce' ||
        cust.customization.SubGroupName === 'NFLSundayTicketMAXUpgrade' ||
        cust.customization.SubGroupName === 'NFLSundayTicket' ||
        cust.customization.SubGroupName === 'ESPNGamePlan' ||
        cust.customization.SubGroupName === 'FoxSoccerPlus' ||
        cust.customization.SubGroupName === 'MLSDirectKick' ||
        cust.customization.SubGroupName === 'National' ||
        cust.customization.SubGroupName === 'Brazilian' ||
        cust.customization.SubGroupName === 'Chinese' ||
        cust.customization.SubGroupName === 'Filipino' ||
        cust.customization.SubGroupName === 'French' ||
        cust.customization.SubGroupName === 'Japanese' ||
        cust.customization.SubGroupName === 'Korean' ||
        cust.customization.SubGroupName === 'Italian' ||
        cust.customization.SubGroupName === 'Russian' ||
        cust.customization.SubGroupName === 'SouthAsian' ||
        cust.customization.SubGroupName === 'Vietnamese' ||
        cust.customization.SubGroupName === 'Spanish' ||
        cust.customization.SubGroupName === 'Regional' ||
        cust.customization.DetailedGroupName === 'Premium' ||
        cust.customization.DetailedGroupName === 'PremiumOptions'

    ) || []);

    closingOffers = computed(() => this.videoPdResponseService.getCustomizations()?.filter(cust => cust.customization.DetailedGroupName === 'ClosingOffers' ||
        cust.customization.DetailedGroupName === 'ClosingOptionalOffers' || 
        cust.customization.DetailedGroupName === 'ClosingAdditionalOffers') || []);

    offers = computed(() => {
        return [(this.state()?.selectedChannels || [])].flat();
    });

    findOfferChoiceCode(choiceCode: string) {
        return this.offers().find(offer => offer?.offers?.some(o => o.choiceCode === choiceCode));
    }

    addIncludedWithPackageOffers() {
        const selectedChannels = (this.state()?.selectedChannels || []).map(channel => ({
            ...channel,
            offers: (channel.offers || []).map(offer => ({ ...offer }))
        }));
        const conflicted = new Set(this.getConflictedIncludedOffers());

        this.customizations().forEach(element => {
            const customization = element.customization;
            const includedOffers = customization.Choices?.Choice?.filter(choice =>
                choice.IncludedWithPackage === true || choice.IncludedWithPackage === 'true'
            ) || [];

            if (includedOffers.length === 0) return;

            let selectedChannel = selectedChannels.find(channel => channel.customizationCode === customization.CustomizationCode);
            if (!selectedChannel) {
                selectedChannel = {
                    offers: [],
                    reviewed: this.state()?.globalReviewed || false,
                    customizationCode: customization.CustomizationCode
                };
                selectedChannels.push(selectedChannel);
            }

            includedOffers.forEach(choice => {
                if (conflicted.has(choice.ChoiceCode)) {
                    selectedChannel.offers = selectedChannel.offers.filter(offer => offer.choiceCode !== choice.ChoiceCode);
                    return;
                }

                const selectedOffer = selectedChannel.offers.find(offer => offer.choiceCode === choice.ChoiceCode);
                if (selectedOffer) {
                    selectedOffer.accepted = true;
                    return;
                }

                selectedChannel.offers.push({
                    choiceCode: choice.ChoiceCode,
                    accepted: true,
                    requiredInput: false,
                    customizationCode: customization.CustomizationCode
                });
            });
        });

        this.updateData({ selectedChannels });
    }

    // Included offers that carry DisabledOfferCodes render with a checkbox and give way to a selected premium offer
    getConflictedIncludedOffers(): string[] {
        const choices = this.customizations().flatMap(cust => cust.customization.Choices?.Choice ?? []);
        const accepted = new Set((this.state()?.selectedChannels ?? [])
            .flatMap(channel => channel?.offers ?? [])
            .filter(offer => offer?.accepted && offer?.choiceCode)
            .map(offer => offer.choiceCode.toUpperCase()));
        const premiums = choices.filter(choice =>
            !this.isIncludedWithPackage(choice) && accepted.has((choice.ChoiceCode ?? '').toUpperCase()));

        return choices
            .filter(choice => this.isIncludedWithCheckbox(choice))
            .filter(choice => {
                const code = (choice.ChoiceCode ?? '').toUpperCase();
                const ownConflicts = this.parseCodes(choice.DisabledOfferCodes);
                return premiums.some(premium =>
                    ownConflicts.includes((premium.ChoiceCode ?? '').toUpperCase()) ||
                    this.parseCodes(premium.DisabledOfferCodes).includes(code));
            })
            .map(choice => choice.ChoiceCode);
    }

    private isIncludedWithPackage(choice: Choice | undefined): boolean {
        return choice?.IncludedWithPackage === true || choice?.IncludedWithPackage === 'true';
    }

    private isIncludedWithCheckbox(choice: Choice | undefined): boolean {
        return this.isIncludedWithPackage(choice) && this.parseCodes(choice?.DisabledOfferCodes).length > 0;
    }

    private parseCodes(raw: string | null | undefined): string[] {
        return (raw ?? '').split(',').map(code => code.trim().toUpperCase()).filter(Boolean);
    }

    protected override saveLocalDraft(): void {
        this.flowStateService.updateData({ [this.stateKey]: this.state() });
    }

    resetData() {
        this.state.set(null);
        this.saveLocalDraft();
    }
}
